import { getOutboxDb } from './outbox.db';
import type { EnqueueInput, OutboxMutation, OutboxStatus } from './types';

/**
 * Error lanzado por `enqueue` cuando IndexedDB rechaza la escritura
 * (quota llena, Safari en modo privado, BD corrupta, permiso denegado).
 * El axios interceptor offline captura esto y lo propaga al caller como
 * error de red sintético con mensaje claro al usuario.
 */
export class OutboxStorageError extends Error {
  readonly cause?: unknown;
  constructor(message: string, cause?: unknown) {
    super(message);
    this.name = 'OutboxStorageError';
    this.cause = cause;
  }
}

/**
 * API estable del mutation outbox. Los call-sites (axios interceptor,
 * sync engine, badge de pendientes) consumen estos helpers; nadie toca
 * Dexie directamente.
 *
 * Todas las funciones son seguras de invocar en cliente solo. Llamarlas
 * en SSR lanzaría error de IndexedDB indefinido — los componentes deben
 * estar marcados como `'use client'`.
 */

/// Encola una nueva mutación. Asume que `id` es UUID v4 (no se valida
/// aquí — class-validator lo hará en el backend y el smoke fallaría
/// inmediatamente; el axios interceptor garantiza la forma).
///
/// Si IndexedDB rechaza la escritura (quota llena, Safari modo privado,
/// BD corrupta), lanza `OutboxStorageError`. El axios interceptor
/// captura y lo propaga al caller como error de red para que el toast
/// muestre al usuario que no se pudo guardar localmente.
export async function enqueue(input: EnqueueInput): Promise<OutboxMutation> {
  const row: OutboxMutation = {
    ...input,
    createdAt: Date.now(),
    attempts: 0,
    status: 'pending',
  };
  try {
    await getOutboxDb().mutations.add(row);
  } catch (e) {
    const err = e as { name?: string; message?: string };
    // QuotaExceededError es la causa más común. Otros casos: BD corrupta,
    // permiso denegado por el browser (private mode iOS), constraint.
    const isQuota = err?.name === 'QuotaExceededError';
    throw new OutboxStorageError(
      isQuota
        ? 'Almacenamiento offline lleno. Libera espacio o conéctate para sincronizar.'
        : `No se pudo guardar la operación offline: ${err?.message ?? 'storage error'}`,
      e,
    );
  }
  emit('outbox:enqueued', { id: row.id, entityType: row.entityType });
  return row;
}

/// Devuelve TODAS las pendientes del usuario, en orden FIFO por
/// `createdAt`. El sync engine las despacha así sin necesidad de
/// resolver dependencias (los IDs los genera el cliente antes de
/// encolar, así que las referencias entre mutaciones ya apuntan al
/// UUID definitivo).
export async function listPending(userId: string): Promise<OutboxMutation[]> {
  return getOutboxDb()
    .mutations.where('[userId+status]')
    .equals([userId, 'pending'])
    .sortBy('createdAt');
}

/// Marca como `sending`. Lo llama el sync engine ANTES de hacer el
/// request HTTP, para que múltiples pestañas (caso raro: si el Web Lock
/// fallara) no procesen la misma mutación.
export async function markSending(id: string): Promise<void> {
  await updateStatus(id, 'sending');
}

/// Marca como `pending` de nuevo. Se usa cuando un intento falla con
/// error transitorio (5xx/network) y la mutación debe quedar disponible
/// para el siguiente drain. Incrementa `attempts` y guarda `lastError`.
export async function markRetry(id: string, error: string): Promise<void> {
  const db = getOutboxDb();
  await db.mutations.update(id, (row) => {
    row.status = 'pending';
    row.attempts = (row.attempts ?? 0) + 1;
    row.lastError = truncate(error, 500);
  });
}

/// Marca como completada con éxito (2xx). Lanza el evento
/// `outbox:sent` para que los listeners (sync engine, React Query
/// invalidator, badge) reaccionen.
export async function markDone(id: string): Promise<void> {
  const db = getOutboxDb();
  const before = await db.mutations.get(id);
  await db.mutations.update(id, (row) => {
    row.status = 'done';
    row.lastError = undefined;
  });
  if (before) {
    emit('outbox:sent', {
      id,
      entityType: before.entityType,
      entityId: before.entityId,
    });
  }
}

/// Marca como `failed` (4xx no recuperable o tope de intentos). El
/// caller debería mostrar un toast al usuario y dar opción de reintentar
/// manualmente.
export async function markFailed(id: string, error: string): Promise<void> {
  const db = getOutboxDb();
  const before = await db.mutations.get(id);
  await db.mutations.update(id, (row) => {
    row.status = 'failed';
    row.attempts = (row.attempts ?? 0) + 1;
    row.lastError = truncate(error, 500);
  });
  if (before) {
    emit('outbox:failed', {
      id,
      entityType: before.entityType,
      entityId: before.entityId,
      error,
    });
  }
}

/// Conteo de pendientes (+sending) para el usuario actual. Usado por el
/// `PendingBadge` (Fase 5). Es una agregación cheap sobre el índice
/// compuesto.
export async function countPending(userId: string): Promise<number> {
  const db = getOutboxDb();
  const [pending, sending] = await Promise.all([
    db.mutations.where('[userId+status]').equals([userId, 'pending']).count(),
    db.mutations.where('[userId+status]').equals([userId, 'sending']).count(),
  ]);
  return pending + sending;
}

/// Recupera una mutación por id (útil para el sync engine cuando recibe
/// solo el id en un evento). Devuelve `undefined` si no existe.
export async function getById(id: string): Promise<OutboxMutation | undefined> {
  return getOutboxDb().mutations.get(id);
}

/// Devuelve TODAS las mutaciones del usuario, cualquier estado. Útil
/// para una página de diagnóstico/admin offline.
export async function listAll(userId: string): Promise<OutboxMutation[]> {
  return getOutboxDb()
    .mutations.where('userId')
    .equals(userId)
    .sortBy('createdAt');
}

/// Borra las mutaciones `done` más viejas que `olderThanMs`. Mantener
/// el historial reciente facilita el debug; borrar limpia el storage.
/// Default: 7 días.
export async function purgeDone(
  olderThanMs: number = 7 * 24 * 60 * 60 * 1000,
): Promise<number> {
  const db = getOutboxDb();
  const cutoff = Date.now() - olderThanMs;
  const ids: string[] = [];
  await db.mutations
    .where('status')
    .equals('done')
    .each((row) => {
      if (row.createdAt < cutoff) ids.push(row.id);
    });
  if (ids.length > 0) await db.mutations.bulkDelete(ids);
  return ids.length;
}

/// Reinicia los `failed` para reintentar manualmente (acción del user
/// "Reintentar todo" tras corregir un problema). Resetea `attempts` y
/// `lastError`, pone `status = 'pending'`. Transaccional: si entre el
/// SELECT y los UPDATE otra pestaña marca una fila como `done`,
/// re-leemos cada fila dentro de la transacción para asegurarnos de
/// que sigue en estado `failed` antes de cambiarla.
export async function retryFailed(userId: string): Promise<number> {
  const db = getOutboxDb();
  let changed = 0;
  await db.transaction('rw', db.mutations, async () => {
    const failed = await db.mutations
      .where('[userId+status]')
      .equals([userId, 'failed'])
      .toArray();
    for (const row of failed) {
      // Re-lectura dentro de la transacción: si otra pestaña ya la
      // marcó done/sending, no debemos resetear su estado.
      const current = await db.mutations.get(row.id);
      if (!current || current.status !== 'failed') continue;
      await db.mutations.update(row.id, {
        status: 'pending',
        attempts: 0,
        lastError: undefined,
      });
      changed++;
    }
  });
  return changed;
}

// ─── Helpers internos ────────────────────────────────────────────────

async function updateStatus(id: string, status: OutboxStatus): Promise<void> {
  await getOutboxDb().mutations.update(id, { status });
}

function truncate(s: string, max: number): string {
  return s.length <= max ? s : s.slice(0, max - 1) + '…';
}

function emit(event: string, detail: unknown): void {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent(event, { detail }));
}
